import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  LogIn,
  Loader2,
  User,
  Eye,
  EyeOff,
  Mail,
  CheckCircle,
  Sparkles,
  ShieldCheck,
  Truck,
  Zap,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import Navbar from './Navbar';
import Footer from './Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Emergent-managed Google Auth: redirects user to https://auth.emergentagent.com/?redirect=<this-page>
// then returns with `#session_id=...` in the URL hash. We exchange that for an internal session.
const EMERGENT_AUTH_URL = 'https://auth.emergentagent.com';

const CustomerLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [mode, setMode] = useState('password'); // 'password' | 'magic'
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  // Pre-fill email from ?email=... in the URL at first render (no useEffect needed)
  const [formData, setFormData] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return { email: params.get('email') || '', password: '' };
  });

  // Read redirect target from query string (?redirect=/some/path); default to /dashboard
  const redirectTarget = (() => {
    const params = new URLSearchParams(location.search);
    return params.get('redirect') || '/dashboard';
  })();

  const persistCustomerSession = useCallback((data) => {
    sessionStorage.setItem(
      'customer',
      JSON.stringify({
        session_token: data.session_token,
        customer_id: data.customer_id,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone || '',
        city: data.city || '',
      })
    );
  }, []);

  // Handle Google auth callback: Emergent returns with #session_id=... in the URL fragment.
  // The async work is intentionally fired from this effect on mount; the setState calls
  // happen inside the awaited promise resolution (i.e. after the effect has returned).
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || !hash.includes('session_id=')) return undefined;

    const sessionId = hash.split('session_id=')[1]?.split('&')[0];
    if (!sessionId) return undefined;

    // Clear the hash so a refresh doesn't replay it
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    let cancelled = false;
    const exchangeSession = async () => {
      setGoogleLoading(true);
      setError('');
      try {
        const response = await axios.post(`${API}/auth/google/session`, {
          session_id: sessionId,
        });
        if (cancelled) return;
        persistCustomerSession(response.data);
        navigate(redirectTarget);
      } catch (err) {
        if (cancelled) return;
        const message =
          err.response?.data?.detail || 'Google sign-in failed. Please try again.';
        setError(message);
      } finally {
        if (!cancelled) setGoogleLoading(false);
      }
    };

    exchangeSession();
    return () => {
      cancelled = true;
    };
  }, [navigate, redirectTarget, persistCustomerSession]);

  const handleGoogleSignIn = () => {
    // Build a return URL that preserves the redirect target so we still
    // route the user correctly after the Google round-trip.
    const returnUrl = new URL(window.location.origin + '/customer/login');
    if (redirectTarget !== '/dashboard') {
      returnUrl.searchParams.set('redirect', redirectTarget);
    }
    const authUrl = `${EMERGENT_AUTH_URL}/?redirect=${encodeURIComponent(returnUrl.toString())}`;
    window.location.href = authUrl;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
    setMagicLinkSent(false);
  };

  const isEmailValid = (email) => /\S+@\S+\.\S+/.test(email);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!isEmailValid(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!formData.password) {
      setError('Please enter your password');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/customers/login`, {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      });

      persistCustomerSession(response.data);
      navigate(redirectTarget);
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!isEmailValid(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/magic-link/request`, {
        email: formData.email.trim().toLowerCase(),
      });
      setMagicLinkSent(true);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to send magic link. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <Navbar />

      <section className="relative flex-1 flex items-center overflow-hidden pt-24 pb-12 lg:py-0">
        {/* Background gradients (matches HeroSection) */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-transparent to-teal-900/20" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-amber-500 transition-colors mb-6 lg:mb-10"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('common.back') || 'Back'}</span>
          </button>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* LEFT — Marketing pitch (hidden on small, shown lg+) */}
            <div className="hidden lg:block">
              <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-2 mb-8">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span className="text-amber-500 text-sm font-medium">For Customers</span>
              </div>

              <h2 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
                Track every quote,<br />
                <span className="text-amber-500">every trip</span> — in one place.
              </h2>
              <p className="text-gray-400 text-lg mb-10 max-w-lg leading-relaxed">
                Sign in to manage your booking requests, compare transporter quotes
                side-by-side, and pay safely with CMI — all from one dashboard.
              </p>

              <div className="space-y-5">
                {[
                  {
                    icon: Truck,
                    title: 'Save your trips & quotes',
                    desc: 'Every request is stored — pick up where you left off, any time.',
                  },
                  {
                    icon: ShieldCheck,
                    title: 'Pay safely with CMI',
                    desc: 'Moroccan-grade payment security with a single tap inside the app.',
                  },
                  {
                    icon: Zap,
                    title: 'Faster quote responses',
                    desc: 'Get push-notified the moment a verified transporter quotes your trip.',
                  },
                ].map((feature) => (
                  <div key={feature.title} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-11 h-11 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center justify-center">
                      <feature.icon className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">{feature.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — Auth card (full width on mobile) */}
            <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
              <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-6 sm:p-8 md:p-10 backdrop-blur-sm shadow-2xl">
                <div className="text-center mb-6 sm:mb-8">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <User className="w-7 h-7 sm:w-8 sm:h-8 text-amber-500" />
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Welcome back</h1>
                  <p className="text-gray-400 text-sm sm:text-base">Sign in to manage your trips and quotes.</p>
                </div>

            {/* Google Sign-In */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
              className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 disabled:opacity-60 text-gray-900 font-semibold py-3 rounded-xl transition-colors mb-4"
              data-testid="google-signin-btn"
            >
              {googleLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Signing in with Google...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>Continue with Google</span>
                </>
              )}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-gray-500 text-xs uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            {/* Tab switcher */}
            <div className="flex bg-black/40 border border-white/10 rounded-xl p-1 mb-6">
              <button
                type="button"
                onClick={() => {
                  setMode('password');
                  setError('');
                  setMagicLinkSent(false);
                }}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  mode === 'password'
                    ? 'bg-amber-500 text-black'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="tab-password"
              >
                Password
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('magic');
                  setError('');
                }}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  mode === 'magic'
                    ? 'bg-amber-500 text-black'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="tab-magic-link"
              >
                Magic link
              </button>
            </div>

            {error && (
              <div
                className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6"
                data-testid="customer-login-error"
              >
                <p className="text-red-500 text-sm text-center">{error}</p>
              </div>
            )}

            {magicLinkSent && (
              <div
                className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-6 flex items-start gap-3"
                data-testid="magic-link-sent-banner"
              >
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-emerald-500 text-sm font-semibold">Check your email</p>
                  <p className="text-gray-300 text-xs mt-1">
                    We sent a sign-in link to {formData.email}. It expires in 15 minutes.
                  </p>
                </div>
              </div>
            )}

            <form
              onSubmit={mode === 'password' ? handlePasswordLogin : handleMagicLink}
              className="space-y-5"
              data-testid="customer-login-form"
            >
              <div>
                <Label className="text-gray-300 mb-2 block">Email address</Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl"
                  data-testid="login-email-input"
                />
              </div>

              {mode === 'password' && (
                <div>
                  <Label className="text-gray-300 mb-2 block">Password</Label>
                  <div className="relative">
                    <Input
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Your password"
                      className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl pr-12"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                      data-testid="login-toggle-password-btn"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              )}

              {mode === 'magic' && (
                <p className="text-sm text-gray-400 leading-relaxed bg-white/5 rounded-xl p-4 border border-white/10">
                  We&apos;ll email you a one-tap sign-in link. No password needed.
                </p>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-6 rounded-full text-lg"
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {mode === 'password' ? 'Signing in...' : 'Sending link...'}
                  </>
                ) : mode === 'password' ? (
                  <>
                    <LogIn className="w-5 h-5 mr-2" />
                    Sign in
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5 mr-2" />
                    Send magic link
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 sm:mt-8 pt-6 border-t border-white/10 text-center">
              <p className="text-gray-400 mb-2 text-sm">New to HorseGO?</p>
              <Link
                to="/customer/register"
                className="text-amber-500 font-semibold hover:text-amber-400 transition-colors"
                data-testid="go-to-register-link"
              >
                Create an account →
              </Link>
            </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CustomerLoginPage;
